import { useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_LABEL } from "@/lib/constants";

const STORAGE_KEY = "sl_last_statuses_v1";
const POLL_MS = 60_000; // 1 minute

function readMap() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch (_e) { return {}; }
}
function writeMap(m) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(m)); } catch (_e) { /* ignore */ }
}

function fireNotification(tx, prev) {
  if (typeof window === "undefined") return;
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const newStatus = STATUS_LABEL[tx.status] || tx.status;
  const oldStatus = prev ? (STATUS_LABEL[prev] || prev) : null;
  const title = `Swiftlink-Africa · ${tx.code}`;
  const body = oldStatus
    ? `Statut mis à jour : ${oldStatus} → ${newStatus}`
    : `Nouveau statut : ${newStatus}`;
  try {
    const n = new Notification(title, {
      body,
      icon: "/brand/swiftlink-logo.png",
      tag: `tx-${tx.code}`,
      data: { code: tx.code },
    });
    n.onclick = () => {
      window.focus();
      window.location.assign(`/suivi?code=${tx.code}`);
    };
  } catch (_e) { /* ignore (some browsers require SW for notifications) */ }
}

export default function useStatusWatcher() {
  const { user } = useAuth();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/me/transactions");
        if (cancelled || !Array.isArray(data)) return;
        const prev = readMap();
        const next = { ...prev };
        let changed = false;
        for (const t of data) {
          const lastSeen = prev[t.code];
          if (lastSeen && lastSeen !== t.status) {
            fireNotification(t, lastSeen);
          }
          next[t.code] = t.status;
          changed = changed || (lastSeen !== t.status);
        }
        if (changed) writeMap(next);
        else writeMap(next); // keep map fresh anyway
      } catch (_e) {
        /* network errors are fine, retry next tick */
      }
    };

    // First check after a small delay to let auth settle
    const initial = setTimeout(tick, 4000);
    timerRef.current = setInterval(tick, POLL_MS);

    return () => {
      cancelled = true;
      clearTimeout(initial);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user]);
}
